import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import TextArea from "../../Shared/TextArea";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import {
  useBookingPage,
  useUpdatePoliciesTab,
} from "../../../hooks/useBookingPage";
import TextAreaFormSkeleton from "../../Shared/LoadingSkeletons/TextAreaFormSkeleton";

const CancellationPolicyForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const maxCharacters = 1000;

  // Fetch config data
  const { data: configData, isLoading } = useBookingPage();

  // Update mutation for policies tab
  const { mutate: updatePolicies, isPending: isUpdating } =
    useUpdatePoliciesTab();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      cancellationPolicy: "",
    },
  });

  // Watch form values for character count
  const cancellationPolicy = watch("cancellationPolicy");

  // Set form values when data loads
  useEffect(() => {
    if (configData?.data) {
      const formData = {
        cancellationPolicy: configData.data.cancellationPolicy || "",
      };
      reset(formData, { keepDirty: false });
      setCharCount(configData.data.cancellationPolicy?.length || 0);
    }
  }, [configData, reset]);

  // Update character count
  useEffect(() => {
    setCharCount(cancellationPolicy?.length || 0);
  }, [cancellationPolicy]);

  const onSubmit = async (data) => {
    setErrors({});
    setSuccess(false);

    const formData = {
      cancellationPolicy: data.cancellationPolicy || null,
    };

    updatePolicies(formData, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage || "Failed to update cancellation policy";

        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({
          title: "Update Failed",
          message: displayMessage,
        });
      },
    });
  };


  if (isLoading) {
    return (
      <TabFormWrapper>
        <TextAreaFormSkeleton />
      </TabFormWrapper>
    );
  }

  return (
    <TabFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <div className="px-4 py-6 sm:p-8">
        <div className="space-y-6">
          <TextArea
            label="Cancellation Policy"
            name="cancellationPolicy"
            helperText="Optional — leave blank to skip. Clients will see this on your booking page if set."
            placeholder="e.g., Cancellations must be made at least 24 hours before your appointment. Late cancellations may incur a fee of 50% of the service cost."
            charCount={charCount}
            maxLength={maxCharacters}
            {...register("cancellationPolicy")}
          />
        </div>
      </div>

      <FormFooter
        primaryCaption="Save Changes"
        isLoading={isUpdating}
        disabled={!isDirty}
        requiresVerification={false}
      />

      {/* Alerts */}
      {errors.title && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertRed title={errors.title} message={errors.message} />
        </div>
      )}

      {success && (
        <div className="px-4 sm:px-6 lg:px-8 py-6">
          <AlertGreen
            title="Success"
            message="Cancellation policy updated successfully"
          />
        </div>
      )}
    </TabFormWrapper>
  );
};

export default CancellationPolicyForm;
